import * as React from 'react';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { addToWaitList } from 'Services/Api/_HumanWritten/WaitlistService';
import { getMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import alertToast from 'Util/ToastifyUtils';
import { isEmail } from 'Validators/Functions/Email';
import { CheckboxGroup } from 'Views/Components/Checkbox/CheckboxGroup';
import { Flex } from 'Views/Components/Flex/Flex';
import If from 'Views/Components/If/If';
import { modalWithCustomInput } from 'Views/Components/Modal/CustomModal';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { fetchSeatingTabData, SeatingTabClass } from 'Services/Api/_HumanWritten/SeatingService';
import { BookingWizardData } from '../BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

export interface AddToWaitListModalProps {
	waitlistModel: {
		email: string;
		userId?: string;
		emailError: string;
		notificationTypeError: string;
		joinPassengerWaitlist: boolean;
		joinVehicleWaitlist: boolean;
		joinTrailerWaitlist: boolean;
		/** How many are travelling. Drives both the fare-type check and the all-or-nothing choice. */
		partySize: number;
		/** False to hold the email back until every one of them fits. */
		notifyOnPartialSpace: boolean;
		/** The fare type wanted, or '' for any. Only ever set where the site sells fare types. */
		passengerClassId: string;
	};
	/** The trip being waited on, so the fare types on offer can be looked up. */
	ferryTripId?: string;
	hasInitialCargo: boolean;
	/**
	 * Whether a walk-on fare is something this customer could actually book on this trip — the
	 * operator sells walk-ons and the vessel runs them. False hides the walk-on option, because a
	 * waitlist notification is an invitation to go and book, and the serverside suppresses the
	 * matching email anyway (see WaitlistItemService). Defaults to true so the staff-side caller,
	 * where the restriction does not apply, needs no change.
	 */
	walkOnsOffered?: boolean;
}

/**
 * The types actually on offer in this modal. Vehicle and trailer are only shown when the vessel
 * carries vehicles and the client has that type enabled, but the wizard pre-ticks both from its
 * vehicle tab — so a client with trailers turned off would otherwise submit a trailer subscription
 * against a checkbox that was never rendered.
 */
const selectedNotificationTypes = (
	waitlistModel: AddToWaitListModalProps['waitlistModel'],
	hasInitialCargo: boolean,
	walkOnsOffered: boolean = true,
) => ({
	joinPassengerWaitlist: waitlistModel.joinPassengerWaitlist && walkOnsOffered,
	joinVehicleWaitlist: waitlistModel.joinVehicleWaitlist && appConfigStore.vehicleEnabled && hasInitialCargo,
	joinTrailerWaitlist: waitlistModel.joinTrailerWaitlist && appConfigStore.trailersEnabled && hasInitialCargo,
});

/**
 * At least one notification type has to be chosen — an entry with none selected subscribes the
 * address to nothing, so the server writes no rows and the customer would be told they had joined a
 * list they are not on.
 */
const hasNotificationType = (
	waitlistModel: AddToWaitListModalProps['waitlistModel'],
	hasInitialCargo: boolean,
	walkOnsOffered: boolean = true,
) => {
	const selected = selectedNotificationTypes(waitlistModel, hasInitialCargo, walkOnsOffered);
	return selected.joinPassengerWaitlist || selected.joinVehicleWaitlist || selected.joinTrailerWaitlist;
};

export const ModalContent = observer(
	({ waitlistModel, hasInitialCargo, walkOnsOffered = true, ferryTripId }: AddToWaitListModalProps) => {
		/*
			The fare types this sailing sells, for the "which one are you waiting for" picker.

			Read from the wizard's own seating endpoint rather than the admin one — this modal is
			opened by customers. Only asked for where the product sells fare types, and a failure is
			swallowed: a waitlist entry with no fare type means "any", which is a perfectly good
			answer and better than refusing to let somebody join.
		*/
		const [classes, setClasses] = React.useState<SeatingTabClass[]>([]);
		const wantsClasses = appConfigStore.passengerClassesEnabled && !!ferryTripId;

		React.useEffect(() => {
			if (!wantsClasses || !ferryTripId) {
				return undefined;
			}
			let live = true;
			fetchSeatingTabData(ferryTripId)
				.then(data => {
					if (live) {
						setClasses(data?.passengerClasses ?? []);
					}
				})
				.catch(() => undefined);
			return () => {
				live = false;
			};
		}, [wantsClasses, ferryTripId]);

		// "Any" first, so the default reads as a deliberate choice rather than an empty field.
		const fareTypeOptions = React.useMemo(
			() => [{ label: 'Any fare type', value: '' }, ...classes.map(x => ({ label: x.name, value: x.id }))],
			[classes],
		);

		return (
			<div className="add-to-waitlist-modal-content">
				{/* eslint-disable-next-line max-len */}
				<p className="modal-description">
					<FormattedMessage
						id="waitlist-modal__description"
						defaultMessage="Everyone on this notification list will receive an email when a space becomes available. First in, best dressed 😊."
					/>
				</p>
				{/*
					Said out loud because joining again is the only way to change an answer — there is no
					edit screen for a waitlist entry — and this modal opens with its defaults rather than
					what is already on file. Somebody re-joining to change one thing would otherwise reset
					the other two without ever being told. Looking the entry up to pre-fill it is the
					obvious alternative and is worse: it would let anyone type an address and learn that
					person's travel plans.
				*/}
				<p className="modal-description">
					<FormattedMessage
						id="waitlist-modal__replaces-previous"
						defaultMessage="Joining again replaces any answers you gave before."
					/>
				</p>
				<div data-test-id="waitlist-add-email">
					<TextInput
						id="cancellation-refund-textfield"
						className="email-textfield"
						label="Email"
						required
						placeholder="Enter email"
						value={waitlistModel.email ?? ''}
						error={waitlistModel.emailError}
						onChange={e =>
							runInAction(() => {
								waitlistModel.email = e.target.value;
								if (waitlistModel.emailError !== '') {
									waitlistModel.emailError = '';
								}
							})
						}
					/>
				</div>
				<CheckboxGroup
					label={
						<>
							Type of notification <span className="text-[var(--colour-brand-primary)]">*</span>
						</>
					}
				>
					<Flex direction="col" gap="xxs">
						<If condition={walkOnsOffered}>
							<div data-test-id="waitlist-add-notification-type-passenger">
								<CheckboxInput
									label={`${getPascalCaseMessageByKey('passenger__name__label', 'walk-on')}`}
									checked={waitlistModel.joinPassengerWaitlist ?? false}
									onChange={e =>
										runInAction(() => {
											waitlistModel.joinPassengerWaitlist = e.target.checked;
											if (waitlistModel.notificationTypeError !== '') {
												waitlistModel.notificationTypeError = '';
											}
										})
									}
								/>
							</div>
						</If>
						<If condition={appConfigStore.vehicleEnabled && hasInitialCargo}>
							<div data-test-id="waitlist-add-notification-type-vehicle">
								<CheckboxInput
									label={`${getPascalCaseMessageByKey('vehicle__name__label', 'walk-on')}`}
									checked={waitlistModel.joinVehicleWaitlist ?? false}
									onChange={e =>
										runInAction(() => {
											waitlistModel.joinVehicleWaitlist = e.target.checked;
											if (waitlistModel.notificationTypeError !== '') {
												waitlistModel.notificationTypeError = '';
											}
										})
									}
								/>
							</div>
						</If>
						<If condition={appConfigStore.trailersEnabled && hasInitialCargo}>
							<div data-test-id="waitlist-add-notification-type-trailer">
								<CheckboxInput
									label="Trailer"
									checked={waitlistModel.joinTrailerWaitlist ?? false}
									onChange={e =>
										runInAction(() => {
											waitlistModel.joinTrailerWaitlist = e.target.checked;
											if (waitlistModel.notificationTypeError !== '') {
												waitlistModel.notificationTypeError = '';
											}
										})
									}
								/>
							</div>
						</If>
					</Flex>
					<ErrorMessage error={waitlistModel.notificationTypeError} />
				</CheckboxGroup>

				{/*
					How many, and whether a partial opening is worth hearing about.

					Shown for the walk-on subscription only, and on every site — a operator that sells
					no fare types still has families who would rather not be told about a single seat.
					The fare type picker above is the part that needs the feature flag.
				*/}
				<If condition={waitlistModel.joinPassengerWaitlist && walkOnsOffered}>
					<Flex direction="col" gap="xs" className="waitlist-party">
						<div data-test-id="waitlist-add-party-size">
							<NumberInput
								label="How many passengers?"
								min={1}
								value={waitlistModel.partySize}
								onChange={value =>
									runInAction(() => {
										waitlistModel.partySize = Math.max(1, Number(value) || 1);
									})
								}
							/>
						</div>

						<If condition={classes.length > 0}>
							<div data-test-id="waitlist-add-fare-type">
								<SelectInput
									label="Fare type"
									options={fareTypeOptions}
									value={
										fareTypeOptions.find(
											(o: { value: string }) => o.value === waitlistModel.passengerClassId,
										) ?? fareTypeOptions[0]
									}
									onChange={(option: any) =>
										runInAction(() => {
											waitlistModel.passengerClassId = option?.value ?? '';
										})
									}
								/>
							</div>
						</If>

						{/* Meaningless for a party of one, who is told about any seat either way. */}
						<If condition={waitlistModel.partySize > 1}>
							<div data-test-id="waitlist-add-whole-party">
								<CheckboxInput
									label={`Only tell me when there is room for all ${waitlistModel.partySize}`}
									checked={!waitlistModel.notifyOnPartialSpace}
									onChange={e =>
										runInAction(() => {
											waitlistModel.notifyOnPartialSpace = !e.target.checked;
										})
									}
								/>
							</div>
						</If>
					</Flex>
				</If>
			</div>
		);
	},
);

export const RenderAddToWaitListModal = async (
	ferryTripId: string,
	hasInitialCargo: boolean,
	wizardData?: BookingWizardData,
	loggedInUserId?: string,
	loggedInUserEmail?: string,
	refresh?: () => void,
	isStaff: boolean = false,
	/**
	 * Whether a walk-on fare is bookable on this trip at all. Defaults to true; the staff caller
	 * leaves it alone, since the vessel restriction is customer-facing only.
	 */
	walkOnsOffered: boolean = true,
) => {
	// When only one notification type is on offer there is nothing to choose, so it starts ticked
	// rather than making the booker tick the only box before they can confirm.
	const offered = {
		passenger: walkOnsOffered,
		vehicle: appConfigStore.vehicleEnabled && hasInitialCargo,
		trailer: appConfigStore.trailersEnabled && hasInitialCargo,
	};
	const onlyOffered = Object.values(offered).filter(Boolean).length === 1;

	const waitlistModel = observable({
		// `===` already yields false when `wizardData` is undefined, so no fallback is needed.
		joinPassengerWaitlist: wizardData?.tabSelected === 'passenger' || (onlyOffered && offered.passenger),
		joinVehicleWaitlist: wizardData?.tabSelected === 'vehicle' || (onlyOffered && offered.vehicle),
		joinTrailerWaitlist: wizardData?.tabSelected === 'vehicle' || (onlyOffered && offered.trailer),
		email: !isStaff ? (loggedInUserEmail ?? '') : '',
		userId: !isStaff ? loggedInUserId : '',
		emailError: '',
		notificationTypeError: '',

		// Defaults that behave exactly as this did before it asked: one passenger, any fare type,
		// and tell me about any space at all.
		partySize: 1,
		notifyOnPartialSpace: true,
		passengerClassId: '',
	});

	const ObservableModalContent = observer(() => {
		return (
			<ModalContent
				waitlistModel={waitlistModel}
				hasInitialCargo={hasInitialCargo}
				walkOnsOffered={walkOnsOffered}
				ferryTripId={ferryTripId}
			/>
		);
	});

	const confirmed = await modalWithCustomInput(
		getMessageByKey('add-to-waitlist__action-name', 'Notify me'),
		<ObservableModalContent />,
		{
			cancelText: 'Cancel',
			confirmText: 'Confirm',
			actionClassName: 'add-to-waitlist-modal',
			resolveOnCancel: true,
			onConfirm: action(() => {
				// Both fields are validated before returning, so every problem is shown at once
				// rather than one per attempt.
				if (waitlistModel.email === '') {
					waitlistModel.emailError = 'Email address is required';
				} else if (!isEmail(waitlistModel.email.trim())) {
					waitlistModel.emailError = 'Please enter a valid email address';
				} else {
					waitlistModel.emailError = '';
				}

				waitlistModel.notificationTypeError = hasNotificationType(
					waitlistModel,
					hasInitialCargo,
					walkOnsOffered,
				)
					? ''
					: 'Select at least one type of notification';

				return waitlistModel.emailError === '' && waitlistModel.notificationTypeError === '';
			}),
		},
	);

	if (!confirmed) {
		return;
	}

	try {
		await addToWaitList({
			ferryTripId: ferryTripId,
			userId: loggedInUserId,
			...selectedNotificationTypes(waitlistModel, hasInitialCargo, walkOnsOffered),
			email: waitlistModel.email.trim(),

			// Passenger preferences. Sent only with a walk-on subscription — the server ignores them
			// on a vehicle row, and sending them anyway would imply a party size a vehicle has not got.
			...(waitlistModel.joinPassengerWaitlist && walkOnsOffered
				? {
						partySize: waitlistModel.partySize,
						notifyOnPartialSpace: waitlistModel.notifyOnPartialSpace,
						passengerClassId: waitlistModel.passengerClassId || undefined,
					}
				: {}),
		});
	} catch {
		// axios rejects on any non-2xx, so the failure has to be caught rather than read off
		// `response.status` — the server answers 400 when the request has no address or no
		// notification type, which the validation above should already have prevented.
		alertToast('Unable to join notification list', 'error');
		return;
	}

	if (refresh) {
		refresh();
		alertToast('Added to waitlist', 'success');
	} else {
		alertToast('Refresh the page', 'warning');
	}
};
